/**
 * QueuePage — 队列管理列表（docs/ui_page 规范）
 * 查询面板（关键词 + 状态，草稿态提交）+ 数据表 (PagedGrid) + 详情/删除 Dialog
 * 顶部统计已移除（由「状态」页面的统计承载）。
 */
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Button,
  Switch,
  Input,
  Dropdown,
  Option,
  Field,
} from '@fluentui/react-components'
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
} from '@fluentui/react-components'
import { queueApi } from '@/api/queue'
import type { QueueItem } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import { formatDate } from '@/lib/format'

const PAGE_SIZE = 10

function formatSize(bytes: number): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

const STATUS_LABELS: Record<string, string> = {
  incoming: '待接收',
  active: '处理中',
  deferred: '已延迟',
  bounced: '已退回',
  delivered: '已送达',
  expired: '已过期',
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'incoming', label: '待接收' },
  { value: 'active', label: '处理中' },
  { value: 'deferred', label: '已延迟' },
  { value: 'bounced', label: '已退回' },
  { value: 'delivered', label: '已送达' },
  { value: 'expired', label: '已过期' },
]

// ── 样式（docs/ui_page §3.1/§5.1/§5.2）──
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '8px 12px',
  },
  queryCard: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'flex-end',
  },
  kwField: {
    flex: '2 0 240px',
    minWidth: '220px',
    flexShrink: 0,
  },
  statusField: {
    flex: '1 0 180px',
    minWidth: '160px',
    flexShrink: 0,
  },
  filterRow: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '12px',
  },
  tableCard: {
    padding: '16px',
  },
  dialogSurface: {
    width: '65vw',
    minWidth: '600px',
    maxWidth: '1000px',
  },
})

// ── 组件 ──
export default function QueuePage() {
  const styles = useStyles()
  const queryClient = useQueryClient()

  // 查询表单草稿态（docs/ui_page §4.1：点「查询」/回车才提交到 query）
  const [draftQ, setDraftQ] = useState('')
  const [draftStatus, setDraftStatus] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState<{ q?: string; status?: string }>({})
  const [page, setPage] = useState(1)

  // 详情 Dialog（行点击弹只读详情，docs/ui_page §4.5）
  const [detailItem, setDetailItem] = useState<QueueItem | null>(null)

  // 多选（docs/ui_page §4.2：工具栏「多选」开关切换 single/multiselect）
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<QueueItem[]>([])

  const deleteMutation = useMutation({
    mutationFn: async (rows: QueueItem[]) => {
      await Promise.all(rows.map((item) => queueApi.delete(item.id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue'] })
      toast.success('删除成功', `已删除 ${selectedRows.length} 项`)
    },
    onError: (err) => {
      toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试')
    },
  })

  const { data: items, isLoading } = useQuery({
    queryKey: ['queue', query.status],
    queryFn: () => queueApi.list(query.status),
  })

  // 关键词前端过滤（后端仅支持 status 透传；对已拉取列表做二次过滤）
  const filtered = useMemo(() => {
    let list = items ?? []
    const kw = query.q?.trim().toLowerCase()
    if (kw) {
      list = list.filter(
        (it) =>
          it.id.toLowerCase().includes(kw) ||
          (it.last_error ?? '').toLowerCase().includes(kw) ||
          JSON.stringify(it).toLowerCase().includes(kw),
      )
    }
    return list
  }, [items, query.q])

  const hasActiveFilter = !!query.q?.trim() || !!query.status

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const applyQuery = () => {
    setQuery({ q: draftQ, status: draftStatus })
    setPage(1)
  }
  const resetQuery = () => {
    setDraftQ('')
    setDraftStatus(undefined)
    setQuery({})
    setPage(1)
  }

  const columns: PagedColumn<QueueItem>[] = [
    {
      key: 'id',
      header: '消息 ID',
      width: '200px',
      render: (item) => <Text weight="semibold">{item.id.slice(0, 8)}</Text>,
    },
    {
      key: 'mail_from',
      header: '发件人',
      width: '200px',
      render: (item) => item.mail_from || '-',
    },
    {
      key: 'rcpt_to',
      header: '收件人',
      width: '240px',
      render: (item) => (item.rcpt_to.length ? item.rcpt_to.join(', ') : '-'),
    },
    {
      key: 'size',
      header: '大小',
      width: '100px',
      render: (item) => formatSize(item.size),
    },
    {
      key: 'status',
      header: '状态',
      width: '120px',
      render: (item) => STATUS_LABELS[item.status] ?? item.status,
    },
    {
      key: 'attempts',
      header: '尝试次数',
      headerTooltip: '当前尝试次数 / 最大尝试次数',
      width: '120px',
      render: (item) => `${item.attempts}/${item.max_attempts}`,
    },
    {
      key: 'last_error',
      header: '最近错误',
      width: '220px',
      render: (item) => item.last_error ?? '-',
    },
    {
      key: 'created_at',
      header: '创建时间',
      width: '160px',
      render: (item) => formatDate(item.created_at),
    },
  ]

  return (
    <div className={styles.root}>
      {/* ① 查询面板（docs/ui_page §3.1） */}
      <Card className={styles.queryCard}>
        <div className={styles.formRow}>
          <Field label="关键词" className={styles.kwField}>
            <Input
              placeholder="搜索消息 ID / 收件人 / 错误"
              value={draftQ}
              onChange={(_, d) => setDraftQ(d.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) applyQuery()
              }}
            />
          </Field>
          <Field label="状态" className={styles.statusField}>
            <Dropdown
              value={draftStatus ? STATUS_LABELS[draftStatus] ?? draftStatus : '全部'}
              selectedOptions={draftStatus ? [draftStatus] : []}
              onOptionSelect={(_, d) => setDraftStatus(d.optionValue)}
            >
              <Option value="">全部</Option>
              {STATUS_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        {hasActiveFilter && (
          <div className={styles.filterRow}>
            {`已应用过滤：${[
              query.q?.trim() ? `关键词 "${query.q.trim()}"` : '',
              query.status ? `状态 ${STATUS_LABELS[query.status] ?? query.status}` : '',
            ]
              .filter(Boolean)
              .join(' · ')}`}
          </div>
        )}

        <div className={styles.actionsRow}>
          <Button appearance="subtle" onClick={resetQuery}>
            重置
          </Button>
          <Button appearance="primary" onClick={applyQuery} disabled={isLoading}>
            查询
          </Button>
        </div>
      </Card>

      {/* ② 数据表（docs/ui_page §4.2） */}
      <Card className={styles.tableCard}>
        <PagedGrid<QueueItem>
          rows={pageItems}
          columns={columns}
          loading={isLoading}
          rowKey={(item) => item.id}
          total={filtered.length}
          page={safePage}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          emptyText="暂无队列数据"
          toolbarLeft={
            <Switch
              checked={selectionMode === 'multiselect'}
              onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
              label="多选"
            />
          }
          selectionMode={selectionMode}
          onSelectionChange={setSelectedRows}
          onDelete={(rows) => deleteMutation.mutateAsync(rows)}
          onRowClick={(item) => setDetailItem(item)}
        />
      </Card>

      {/* ③ 详情 Dialog（行点击只读详情，modalType="alert" + DialogCloseTitle，docs/ui_page §4.5） */}
      <Dialog
        open={!!detailItem}
        onOpenChange={(_, d) => !d.open && setDetailItem(null)}
        modalType="alert"
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="队列消息详情" onClose={() => setDetailItem(null)} />
            <DialogContent>
              {detailItem && (
                <DetailGrid key={detailItem.id}>
                  <DetailField label="消息 ID" value={detailItem.id} mono colSpan />
                  <DetailField label="发件人" value={detailItem.mail_from || '-'} />
                  <DetailField label="收件人" value={detailItem.rcpt_to.length ? detailItem.rcpt_to.join(', ') : '-'} />
                  <DetailField label="大小" value={formatSize(detailItem.size)} />
                  <DetailField label="状态" value={STATUS_LABELS[detailItem.status] ?? detailItem.status} />
                  <DetailField label="尝试次数" value={`${detailItem.attempts}/${detailItem.max_attempts}`} />
                  <DetailField label="接收时间" value={detailItem.received_at ? formatDate(detailItem.received_at) : '-'} />
                  <DetailField label="创建时间" value={formatDate(detailItem.created_at)} />
                  <DetailField label="客户端 IP" value={detailItem.client_ip || '-'} />
                  <DetailField label="HELO 域" value={detailItem.helo_domain || '-'} />
                  <DetailField label="认证用户" value={detailItem.authenticated_user || '-'} />
                  <DetailField label="下次尝试" value={detailItem.next_attempt ? formatDate(detailItem.next_attempt) : '-'} />
                  <DetailField label="最近错误" value={detailItem.last_error ?? '-'} colSpan />
                </DetailGrid>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}